// frontend/src/lib/api.ts
// Cliente API con soporte offline

import { getMarcasEnVuelo, esperarMarcasEnVuelo, solicitarFlushMarcas } from './marcasEnVuelo';

// En desarrollo usamos ruta relativa para aprovechar el proxy de Vite y evitar CORS.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Headers de identificación del cliente (plan de migración §3.1 ítem 2 / B5):
// permiten al backend medir uso por cliente (pwa vs. mobile) y por versión
// durante la convivencia con la app Expo. X-SGCC-Client-Id se omite hasta que
// storage.generateClientId() haya creado el id (primer pull tras el login).
// El backend usa cors() con los defaults: Access-Control-Allow-Headers se
// refleja de Access-Control-Request-Headers en el preflight, así que agregar
// headers custom no rompe CORS.
function clientHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'X-SGCC-Client': 'pwa',
    'X-SGCC-App-Version': __APP_VERSION__,
  };
  try {
    const clienteId = localStorage.getItem('sgcc_cliente_id');
    if (clienteId) headers['X-SGCC-Client-Id'] = clienteId;
  } catch {
    // localStorage inaccesible (modo privado restrictivo): se omite el header
  }
  return headers;
}

export interface Cadete {
  id: number;
  apellido: string;
  nombres: string;
  dni: string;
  localidad?: string;
  partido?: string;
  telefono_principal?: string;
  telefono_alternativo?: string;
  compania?: string;
  activo: boolean;
  estado_institucional?: 'Aspirante a Cadete' | 'Cadete' | 'Oficial Recibido' | 'Excluido' | 'Desestimiento' | 'Ausente a Convocatoria' | 'Traspaso';
  estado_metadata?: {
    fecha?: string;
    motivo?: string;
    descripcion?: string;
    compania_destino?: string;
  };
  legajo?: number | null;
  grado?: string | null;
  grado_id?: number | null;
  numero_aula?: number | null;
  domicilio?: string | null;
  comisaria_jurisdiccional?: string | null;
}

export interface SubMotivoAusencia {
  id: number;
  motivo_ausencia_id: number;
  nombre: string;
  codigo: string;
  activo: boolean;
  orden: number;
}

export interface MotivoAusenciaDB {
  id: number;
  nombre: string;
  codigo: string;
  icono: string;
  requiere_sub_opcion: boolean;
  activo: boolean;
  orden: number;
  sub_motivos: SubMotivoAusencia[];
}

export interface TipoLicencia {
  id: number;
  nombre: string;
  codigo: string;
  descripcion?: string;
  activo: boolean;
}

export interface LicenciaCadete {
  id: string;
  cadete_id: number;
  tipo_licencia_id: number;
  fecha_inicio: string;
  fecha_fin: string | null;
  motivo_detalle?: string;
  documento_adjunto?: string;
  created_by?: number;
  tipo_nombre?: string;
  tipo_codigo?: string;
  cadete_apellido?: string;
  cadete_nombres?: string;
  cadete_dni?: string;
}

// Códigos institucionales de novedad: ACA (ausente con aviso), ASA (sin aviso,
// genera despacho), RMH, ART, LE (Licencia Especial, Art. 50), PE (Permiso
// Especial, Art. 54/56).
export type CodigoNovedad = 'ACA' | 'ASA' | 'RMH' | 'ART' | 'LE' | 'PE';

export interface RegistroPresentismo {
  id?: string;
  local_id?: string;
  cadete_id: number;
  fecha: string;
  hora_local: string;
  estado: 'presente' | 'ausente' | 'tarde';
  motivo?: string;
  // Código de novedad calculado por el backend nuevo (solo en /historial y reportes)
  codigo?: CodigoNovedad | null;
  latitud?: number;
  longitud?: number;
  dispositivo_info?: Record<string, unknown>;
  synced_at?: string;
  created_at?: string;
  // Marca de borrado (tombstone) para desmarcar un registro offline
  eliminado?: boolean;
  // Campos join
  apellido?: string;
  nombres?: string;
  dni?: string;
}

export interface SyncRequest {
  cliente_id: string;
  registros: RegistroPresentismo[];
}

export interface SyncResultadoRegistro {
  success: boolean;
  cadeteId?: number;
  fecha?: string;
  error?: string;
  // Opcional: el backend puede marcar un fallo por registro como transitorio
  // (ej. error de base de datos) para que se reintente en la próxima sync.
  // Si no viene, success === false se considera rechazo definitivo.
  transitorio?: boolean;
  retryable?: boolean;
}

export interface SyncResponse {
  success: boolean;
  data: {
    total: number;
    procesados: number;
    fallidos: number;
    // Un resultado por registro enviado, en el mismo orden.
    resultados: SyncResultadoRegistro[];
    // Hora del servidor (ISO)
    timestamp?: string;
  };
}

export type ResumenPorCodigo = Record<CodigoNovedad, number>;

// GET /api/cadetes/:id/historial?desde&hasta (máx. 366 días)
export interface HistorialCadete {
  cadete: {
    id: number;
    apellido: string;
    nombres: string;
    dni: string;
    legajo?: number | null;
    compania_id?: number | null;
    compania?: string | null;
    estado_institucional?: Cadete['estado_institucional'];
    estado_metadata?: Cadete['estado_metadata'];
  };
  desde: string;
  hasta: string;
  // Registros del cadete en el rango, fecha DESC, cada uno con `codigo`
  registros: RegistroPresentismo[];
  // Licencias solapadas con el rango (con tipo_codigo / tipo_nombre)
  licencias: LicenciaCadete[];
  resumen: {
    presentes: number;
    tardes: number;
    ausentes: number;
    por_codigo: ResumenPorCodigo;
    licencias: number;
  };
}

// GET /api/reportes/novedades?companiaId&desde&hasta
export interface NovedadReporte {
  fecha: string;
  cadete_id: number;
  estado: 'ausente' | 'tarde';
  codigo: CodigoNovedad | null;
  motivo: string | null;
  hora_local: string | null;
}

export interface LicenciaReporte {
  id: string;
  cadete_id: number;
  tipo_codigo: string;
  tipo_nombre: string;
  fecha_inicio: string;
  fecha_fin: string | null;
  motivo_detalle?: string | null;
}

export interface ReporteNovedades {
  compania: { id: number; nombre: string };
  desde: string;
  hasta: string;
  generado_en: string;
  cadetes: Array<Pick<Cadete, 'id' | 'apellido' | 'nombres' | 'dni' | 'legajo' | 'estado_institucional'>>;
  novedades: NovedadReporte[];
  licencias: LicenciaReporte[];
  resumen: {
    por_codigo: ResumenPorCodigo;
    tardes: number;
    total_novedades: number;
    licencias: number;
    dias: number;
  };
}

// GET /api/companias → catálogo con el nombre oficial que lleva el parte
// ("nombre_parte", p. ej. "DÉCIMA COMPAÑÍA")
export interface CompaniaApi {
  id: number;
  nombre: string;
  nombre_parte: string;
}

// GET/PUT /api/companias/:id/parametros → encabezado del parte diario
export interface ParametrosCompania {
  jefe_seccion: string;
  instructores: string;
}

// GET /api/reportes/parte?companiaId&fecha → parte diario oficial de la compañía
export type ClaveSeccionParte = 'AAC' | 'EXC' | 'DES' | 'ACA' | 'ASA' | 'RMH' | 'ART' | 'LE' | 'PE';

export interface FilaParte {
  n: number;
  cadete_id: number;
  nombre: string;
  dni: string;
  motivo: string | null;
  desde: string | null;
  hasta: string | null;
}

export interface SeccionParte {
  clave: ClaveSeccionParte;
  titulo: string;
  cantidad: number;
  filas: FilaParte[];
}

export interface ParteNovedades {
  compania: CompaniaApi;
  fecha: string;
  // Ej. 'MARTES 13 DE OCTUBRE DE 2026'
  fecha_texto: string;
  encabezado: ParametrosCompania;
  totales: {
    convocada: number;
    aac: number;
    exclusiones: number;
    desistimientos: number;
    efectiva: number;
    presentes: number;
    ausentes: number;
  };
  secciones: SeccionParte[];
}

// Error de API con el status HTTP expuesto, para que la sincronización pueda
// distinguir errores definitivos (4xx de validación) de transitorios.
//  - status 0 + code 'NETWORK_ERROR': sin red / timeout.
//  - code 'SERVER_UNAVAILABLE': respuesta no-JSON (ej. HTML 502 de un proxy).
//  - code 'HTTP_ERROR': respuesta JSON con status != 2xx.
export type ApiErrorCode = 'NETWORK_ERROR' | 'SERVER_UNAVAILABLE' | 'HTTP_ERROR';

export class ApiError extends Error {
  status: number;
  code: ApiErrorCode;
  // `code` del cuerpo JSON del servidor, si lo hay (ej. 'PASSWORD_CHANGE_REQUIRED')
  serverCode?: string;

  constructor(message: string, status: number, code: ApiErrorCode, serverCode?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.serverCode = serverCode;
  }
}

// Errores definitivos: reintentar no los va a resolver, la op puede descartarse.
// 403: en este backend es permanente (cadete de otra compañía, usuario sin
// compañía asignada). El 401 NO es definitivo: lo maneja handleUnauthorized.
export const DEFINITIVE_ERROR_STATUSES = [400, 403, 404, 409, 422];

export function isDefinitiveApiError(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    error.code === 'HTTP_ERROR' &&
    DEFINITIVE_ERROR_STATUSES.includes(error.status) &&
    // Contraseña por cambiar: es transitorio (se resuelve al cambiarla); tratarlo
    // como definitivo marcaría las marcas pendientes como rechazadas.
    error.serverCode !== 'PASSWORD_CHANGE_REQUIRED'
  );
}

// Endpoints nuevos (historial, reportes de novedades): si el backend desplegado
// todavía no los tiene responde 404 (o el index.html de fallback → SERVER_UNAVAILABLE 404).
export const MSG_REQUIERE_BACKEND_NUEVO = 'Esta función requiere la versión nueva del servidor.';

export function requiereBackendNuevo(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function esErrorDeRed(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'NETWORK_ERROR';
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

// Evento que dispara request() cuando el servidor exige cambiar la contraseña
export const EVENTO_CAMBIO_PASSWORD = 'sgcc:password-change-required';

export type RolUsuario = 'admin' | 'instructor' | 'jefe';

// Usuario de la sesión (respuesta de login / cambio de contraseña)
export interface UsuarioSesion {
  id: number;
  usuario: string;
  nombres: string;
  scope: RolUsuario | null;
  compania_id: number | null;
  compania_nombre: string | null;
  jerarquia: string | null;
  legajo: string | null;
  debe_cambiar_password: boolean;
}

// Usuario tal como lo lista el panel de administración
export interface UsuarioAdmin extends UsuarioSesion {
  activo: boolean;
  created_at: string;
}

export interface NuevoUsuarioAdmin {
  usuario: string;
  nombres: string;
  password: string;
  scope: RolUsuario;
  compania_id: number | null;
  jerarquia?: string;
  legajo?: string;
}

export interface EdicionUsuarioAdmin {
  nombres: string;
  scope: RolUsuario;
  compania_id: number | null;
  jerarquia?: string;
  legajo?: string;
  activo: boolean;
}

export interface CadeteImportado {
  fila: number;
  apellido: string;
  nombres: string;
  dni: string;
  localidad: string;
  partido: string;
  telefono_principal: string;
  telefono_alternativo: string | null;
  legajo: string | null;
  grado: string | null;
  numero_aula: number | null;
  domicilio: string | null;
  comisaria_jurisdiccional: string | null;
}

export interface FilaRechazada {
  fila: number;
  apellido: string;
  nombres: string;
  dni: string;
  errores: string[];
}

export interface ResultadoImportacion {
  compania_id: number;
  compania: string;
  aplicado: boolean;
  total: number;
  validas: CadeteImportado[];
  rechazadas: FilaRechazada[];
  insertados: number;
}

class APIClient {
  private baseURL: string;
  // Cierre de sesión por 401 ya en curso (puede llegar más de un 401 mientras
  // se espera a las marcas en vuelo).
  private cerrandoSesionPor401 = false;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs = 60000
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('auth_token');

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...clientHeaders(),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    // Timeout para no quedar colgados indefinidamente en 3G marginal.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      let response: Response;
      try {
        response = await fetch(url, { ...config, signal: controller.signal });
      } catch (error) {
        // Cualquier fallo de fetch es un error de red: Chrome dice "Failed to fetch",
        // Safari/iOS "Load failed", Firefox "NetworkError when attempting...".
        // Timeout (AbortError) se trata igual: reintentar en la próxima sync.
        if (
          error instanceof TypeError ||
          (error instanceof DOMException && error.name === 'AbortError') ||
          (error as { name?: string } | null)?.name === 'AbortError'
        ) {
          throw new ApiError('NETWORK_ERROR', 0, 'NETWORK_ERROR');
        }
        throw error;
      }

      // El cuerpo puede no ser JSON (ej. página HTML 502/504 del proxy, o el
      // index.html de fallback). Se trata como error transitorio del servidor.
      let data: any;
      try {
        data = await response.json();
      } catch {
        if (response.status === 401) {
          this.handleUnauthorized();
        }
        throw new ApiError(
          'SERVER_UNAVAILABLE',
          response.ok ? 503 : response.status,
          'SERVER_UNAVAILABLE'
        );
      }

      if (!response.ok) {
        if (response.status === 401) {
          this.handleUnauthorized();
        }
        // El servidor exige que el usuario cambie su contraseña (alta o
        // restablecimiento): App muestra la pantalla de cambio obligatorio.
        if (response.status === 403 && data?.code === 'PASSWORD_CHANGE_REQUIRED') {
          window.dispatchEvent(new CustomEvent(EVENTO_CAMBIO_PASSWORD));
        }
        throw new ApiError(data?.error || `HTTP ${response.status}`, response.status, 'HTTP_ERROR', data?.code);
      }

      return data;
    } catch (error) {
      // Errores de lectura del cuerpo por corte de red a mitad de respuesta.
      if (!(error instanceof ApiError)) {
        if (
          error instanceof TypeError ||
          (error as { name?: string } | null)?.name === 'AbortError'
        ) {
          throw new ApiError('NETWORK_ERROR', 0, 'NETWORK_ERROR');
        }
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Descarga binaria autenticada (archivos generados por el servidor). Misma
  // semántica de errores que request(): NETWORK_ERROR sin red/timeout, 401 →
  // handleUnauthorized, 4xx/5xx → ApiError HTTP_ERROR con el mensaje del JSON si lo hay.
  private async requestBlob(endpoint: string, timeoutMs = 120000): Promise<Blob> {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('auth_token');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      let response: Response;
      try {
        response = await fetch(url, {
          headers: {
            ...clientHeaders(),
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          signal: controller.signal,
        });
      } catch (error) {
        if (
          error instanceof TypeError ||
          (error as { name?: string } | null)?.name === 'AbortError'
        ) {
          throw new ApiError('NETWORK_ERROR', 0, 'NETWORK_ERROR');
        }
        throw error;
      }

      if (!response.ok) {
        if (response.status === 401) {
          this.handleUnauthorized();
        }
        let mensaje = `HTTP ${response.status}`;
        let code: ApiErrorCode = 'HTTP_ERROR';
        try {
          const data = await response.json();
          if (data?.error) mensaje = data.error;
        } catch {
          // Cuerpo no JSON (HTML de proxy / index.html de fallback)
          code = 'SERVER_UNAVAILABLE';
        }
        throw new ApiError(mensaje, response.status, code);
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        // El SW/proxy devolvió el index.html en vez del archivo
        throw new ApiError('SERVER_UNAVAILABLE', 404, 'SERVER_UNAVAILABLE');
      }
      return await response.blob();
    } catch (error) {
      if (!(error instanceof ApiError) && (
        error instanceof TypeError ||
        (error as { name?: string } | null)?.name === 'AbortError'
      )) {
        throw new ApiError('NETWORK_ERROR', 0, 'NETWORK_ERROR');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Sesión vencida o inválida: limpiar credenciales y recargar (una sola vez).
  // Si hay marcas en vuelo (cuenta regresiva de CadeteCard / geolocalización)
  // primero se les pide confirmar YA y se espera hasta 8 s a que queden en
  // IndexedDB, igual que handleLogout en App.tsx: la recarga inmediata las
  // perdería, y con la auto-sync por reconexión/foco un 401 puede llegar sin
  // acción del usuario. El resto del flujo de 401 no cambia (D2 se resuelve en
  // la app nueva).
  private handleUnauthorized(): void {
    if (this.cerrandoSesionPor401) return;
    const hadToken = !!localStorage.getItem('auth_token');
    const limpiarCredenciales = () => {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    };
    if (!hadToken) {
      limpiarCredenciales();
      return;
    }
    this.cerrandoSesionPor401 = true;
    const cerrarYRecargar = () => {
      limpiarCredenciales();
      window.location.reload();
    };
    if (getMarcasEnVuelo() === 0) {
      cerrarYRecargar();
      return;
    }
    console.warn('[API] 401 con marcas en vuelo: se confirman antes de cerrar la sesión');
    solicitarFlushMarcas();
    void esperarMarcasEnVuelo(8000).then((listas) => {
      if (!listas) console.warn('[API] Marcas en vuelo tras 8 s; se cierra la sesión igual');
      cerrarYRecargar();
    });
  }

  // Cadetes
  async getCadetes(): Promise<ApiResponse<Cadete[]>> {
    return this.request<Cadete[]>('/cadetes');
  }

  async searchCadetes(termino: string): Promise<ApiResponse<Cadete[]>> {
    return this.request<Cadete[]>(`/cadetes/search/${encodeURIComponent(termino)}`);
  }

  async getCadeteByDNI(dni: string): Promise<ApiResponse<Cadete>> {
    return this.request<Cadete>(`/cadetes/dni/${dni}`);
  }

  async updateCadete(id: number, payload: Partial<Cadete>): Promise<ApiResponse<Cadete>> {
    return this.request<Cadete>(`/cadetes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async updateGradoCompania(compania: string, grado: string): Promise<ApiResponse<Cadete[]>> {
    return this.request<Cadete[]>('/cadetes/compania/grado', {
      method: 'PUT',
      body: JSON.stringify({ compania, grado }),
    });
  }

  // Historial de un cadete en un rango (backend nuevo; 404 en el viejo)
  async getHistorialCadete(id: number, desde: string, hasta: string): Promise<ApiResponse<HistorialCadete>> {
    const params = new URLSearchParams({ desde, hasta });
    return this.request<HistorialCadete>(`/cadetes/${id}/historial?${params.toString()}`);
  }

  // Registros
  async getRegistros(fechaInicio?: string, fechaFin?: string, cadeteId?: number): Promise<ApiResponse<RegistroPresentismo[]>> {
    const params = new URLSearchParams();
    if (fechaInicio) params.append('fechaInicio', fechaInicio);
    if (fechaFin) params.append('fechaFin', fechaFin);
    if (cadeteId) params.append('cadeteId', String(cadeteId));
    const query = params.toString();
    return this.request<RegistroPresentismo[]>(`/registros${query ? `?${query}` : ''}`);
  }

  async getRegistroHoy(cadeteId: number, fecha: string): Promise<ApiResponse<RegistroPresentismo>> {
    return this.request<RegistroPresentismo>(`/registros/cadete/${cadeteId}/fecha/${fecha}`);
  }

  async getResumenHoy(): Promise<ApiResponse<{
    presentes: number;
    ausentes: number;
    tardes: number;
    total_registros: number;
    porcentaje_asistencia: number;
  }>> {
    return this.request(`/registros/hoy/resumen`);
  }

  async getPendientesHoy(): Promise<ApiResponse<Cadete[]>> {
    return this.request<Cadete[]>('/registros/hoy/pendientes');
  }

  async createRegistro(registro: Omit<RegistroPresentismo, 'id' | 'synced_at' | 'created_at'>): Promise<ApiResponse<RegistroPresentismo>> {
    return this.request<RegistroPresentismo>('/registros', {
      method: 'POST',
      body: JSON.stringify(registro),
    });
  }

  async createRegistrosBatch(registros: Omit<RegistroPresentismo, 'id' | 'synced_at' | 'created_at'>[]): Promise<ApiResponse<{
    total: number;
    exitosos: number;
    fallidos: number;
    resultados: unknown[];
  }>> {
    return this.request('/registros/batch', {
      method: 'POST',
      body: JSON.stringify({ registros }),
    });
  }

  // Sincronización
  async syncOfflineRecords(clienteId: string, registros: RegistroPresentismo[]): Promise<ApiResponse<SyncResponse["data"]>> {
    // Enviar solo los campos que consume el backend (en camelCase), sin
    // metadatos locales (local_id, pending_sync) ni campos de join.
    const registrosNormalizados = registros.map((registro) => ({
      cadeteId: (registro as any).cadeteId ?? registro.cadete_id,
      fecha: registro.fecha,
      horaLocal: (registro as any).horaLocal ?? registro.hora_local ?? null,
      estado: registro.estado,
      motivo: registro.motivo ?? null,
      latitud: registro.latitud ?? null,
      longitud: registro.longitud ?? null,
      dispositivoInfo: (registro as any).dispositivoInfo ?? registro.dispositivo_info ?? null,
      // Tombstone: el backend elimina el registro de cadeteId+fecha
      ...(registro.eliminado ? { eliminado: true } : {}),
    }));

    return this.request<SyncResponse["data"]>("/sync/offline-recibir", {
      method: 'POST',
      body: JSON.stringify({ clienteId, registros: registrosNormalizados }),
    });
  }

  async getCompleteData(clienteId: string, ultimaSincronizacion?: string): Promise<ApiResponse<{
    cadetes: Cadete[];
    registrosRecientes: RegistroPresentismo[];
    sincronizacionesPendientes: unknown[];
    tiposLicencias?: TipoLicencia[];
    licencias?: LicenciaCadete[];
    // Hora del servidor capturada antes de las consultas (puede faltar en backends viejos)
    timestamp?: string;
    // true si registrosRecientes contiene la ventana completa de 30 días del scope
    registrosCompletos?: boolean;
    // Inicio (YYYY-MM-DD, hora de Buenos Aires) de la ventana de registros completa
    registrosDesde?: string;
  }>> {
    const params = ultimaSincronizacion ? `?ultimaSincronizacion=${encodeURIComponent(ultimaSincronizacion)}` : '';
    return this.request(`/sync/datos-completos/${clienteId}${params}`);
  }

  async getSyncStats(): Promise<ApiResponse<{
    pendientes: number;
    sincronizados: number;
    fallidos: number;
    total: number;
  }>> {
    return this.request('/sync/stats');
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await fetch(`${this.baseURL}/../health`);
    return response.json();
  }

  // Inicialización
  async getInitStatus(): Promise<ApiResponse<{
    inicializado: boolean;
    totalCadetes: number;
    mensaje: string;
  }>> {
    return this.request('/init/status');
  }

  async importarCadetes(): Promise<ApiResponse<{
    total: number;
    exitosos: number;
    fallidos: number;
  }>> {
    return this.request('/init/importar-cadetes', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Tipos de Licencias
  async getTiposLicencias(activo?: boolean): Promise<ApiResponse<TipoLicencia[]>> {
    const params = activo !== undefined ? `?activo=${activo}` : '';
    return this.request<TipoLicencia[]>(`/licencias/tipos${params}`);
  }

  async createTipoLicencia(tipo: Omit<TipoLicencia, 'id' | 'activo'>): Promise<ApiResponse<TipoLicencia>> {
    return this.request<TipoLicencia>('/licencias/tipos', {
      method: 'POST',
      body: JSON.stringify(tipo),
    });
  }

  async updateTipoLicencia(id: number, tipo: Partial<TipoLicencia>): Promise<ApiResponse<TipoLicencia>> {
    return this.request<TipoLicencia>(`/licencias/tipos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tipo),
    });
  }

  async deleteTipoLicencia(id: number): Promise<ApiResponse<TipoLicencia>> {
    return this.request<TipoLicencia>(`/licencias/tipos/${id}`, {
      method: 'DELETE',
    });
  }

  // Licencias de Cadetes
  // `rango` (solapamiento con [desde, hasta]) requiere el backend nuevo
  async getLicencias(cadeteId?: number, fecha?: string, rango?: { desde: string; hasta: string }): Promise<ApiResponse<LicenciaCadete[]>> {
    const params = new URLSearchParams();
    if (cadeteId) params.append('cadeteId', cadeteId.toString());
    if (fecha) params.append('fecha', fecha);
    if (rango) {
      params.append('desde', rango.desde);
      params.append('hasta', rango.hasta);
    }
    const query = params.toString();
    return this.request<LicenciaCadete[]>(`/licencias${query ? `?${query}` : ''}`);
  }

  async createLicencia(licencia: Omit<LicenciaCadete, 'id' | 'created_by' | 'tipo_nombre' | 'tipo_codigo'>): Promise<ApiResponse<LicenciaCadete>> {
    const payload = {
      cadeteId: licencia.cadete_id,
      tipoLicenciaId: licencia.tipo_licencia_id,
      fechaInicio: licencia.fecha_inicio,
      fechaFin: licencia.fecha_fin,
      motivoDetalle: licencia.motivo_detalle,
      documentoAdjunto: licencia.documento_adjunto,
    };
    return this.request<LicenciaCadete>('/licencias', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteLicencia(id: string): Promise<ApiResponse<LicenciaCadete>> {
    return this.request<LicenciaCadete>(`/licencias/${id}`, {
      method: 'DELETE',
    });
  }

  // Reporte de novedades y licencias por compañía y período (backend nuevo)
  async getReporteNovedades(companiaId: number, desde: string, hasta: string): Promise<ApiResponse<ReporteNovedades>> {
    const params = new URLSearchParams({ companiaId: String(companiaId), desde, hasta });
    return this.request<ReporteNovedades>(`/reportes/novedades?${params.toString()}`);
  }

  // Mismo reporte como .xlsx generado por el servidor. Hay que pedirlo con
  // Authorization (no sirve un <a href>), así que se descarga el Blob.
  async descargarReporteNovedadesXlsx(companiaId: number, desde: string, hasta: string): Promise<Blob> {
    const params = new URLSearchParams({ companiaId: String(companiaId), desde, hasta, formato: 'xlsx' });
    return this.requestBlob(`/reportes/novedades?${params.toString()}`);
  }

  // ── Parte de Novedades (backend nuevo; 404 en el viejo) ──
  async getCompanias(): Promise<ApiResponse<CompaniaApi[]>> {
    return this.request<CompaniaApi[]>('/companias');
  }

  // Encabezado del parte (jefe de sección / instructores); el backend devuelve
  // valores por defecto si la compañía todavía no tiene fila.
  async getParametrosCompania(id: number): Promise<ApiResponse<ParametrosCompania>> {
    return this.request<ParametrosCompania>(`/companias/${id}/parametros`);
  }

  // Admin, o instructor/jefe de esa compañía
  async putParametrosCompania(id: number, datos: ParametrosCompania): Promise<ApiResponse<ParametrosCompania>> {
    return this.request<ParametrosCompania>(`/companias/${id}/parametros`, {
      method: 'PUT',
      body: JSON.stringify(datos),
    });
  }

  async getParteNovedades(companiaId: number, fecha: string): Promise<ApiResponse<ParteNovedades>> {
    const params = new URLSearchParams({ companiaId: String(companiaId), fecha });
    return this.request<ParteNovedades>(`/reportes/parte?${params.toString()}`);
  }

  // Mismo parte como .xlsx generado por el servidor (descarga autenticada del Blob)
  async descargarParteNovedadesXlsx(companiaId: number, fecha: string): Promise<Blob> {
    const params = new URLSearchParams({ companiaId: String(companiaId), fecha, formato: 'xlsx' });
    return this.requestBlob(`/reportes/parte?${params.toString()}`);
  }

  // Autenticación
  async login(usuario: string, password: string): Promise<ApiResponse<{ token: string; user: UsuarioSesion }>> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ usuario, password }),
    });
  }

  // Cambio de contraseña del propio usuario. Devuelve un token nuevo (el
  // cambio revoca los anteriores). "Actual incorrecta" llega como 400.
  async cambiarPassword(actual: string, nueva: string): Promise<ApiResponse<{ token: string; user: UsuarioSesion }>> {
    return this.request('/auth/cambiar-password', {
      method: 'POST',
      body: JSON.stringify({ actual, nueva }),
    });
  }

  // ── Administración (sólo admin; el servidor responde 404 al resto) ──────
  async adminUsuarios(): Promise<ApiResponse<UsuarioAdmin[]>> {
    return this.request('/admin/usuarios');
  }

  async adminCrearUsuario(datos: NuevoUsuarioAdmin): Promise<ApiResponse<UsuarioAdmin>> {
    return this.request('/admin/usuarios', { method: 'POST', body: JSON.stringify(datos) });
  }

  async adminActualizarUsuario(id: number, datos: EdicionUsuarioAdmin): Promise<ApiResponse<UsuarioAdmin>> {
    return this.request(`/admin/usuarios/${id}`, { method: 'PUT', body: JSON.stringify(datos) });
  }

  async adminRestablecerPassword(id: number, temporal: string): Promise<ApiResponse<UsuarioAdmin>> {
    return this.request(`/admin/usuarios/${id}/restablecer-password`, {
      method: 'POST',
      body: JSON.stringify({ temporal }),
    });
  }

  async adminPlantillaCadetes(): Promise<Blob> {
    return this.requestBlob('/admin/cadetes/plantilla');
  }

  async adminImportarCadetes(companiaId: number, archivoBase64: string, aplicar: boolean): Promise<ApiResponse<ResultadoImportacion>> {
    return this.request('/admin/cadetes/importar', {
      method: 'POST',
      body: JSON.stringify({ compania_id: companiaId, archivoBase64, aplicar }),
    }, 120000);
  }
}

export const api = new APIClient(API_BASE_URL);
